import type { StompConfig } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { api, SILENT_CONFIG } from "@/shared/api/client";
import { buildStompAuthHeaders } from "@/shared/lib/stompAuth";

/**
 * Ticket de uso único (válido 30 s) que o gateway troca pela identidade da conexão
 * no handshake do WebSocket. Pedir um novo a cada ligação e a cada reconexão.
 */
export async function fetchWsTicket(): Promise<string | null> {
    try {
        const response = await api.post<{ ticket?: string }>("/ws-ticket", undefined, SILENT_CONFIG);
        return response.data?.ticket ?? null;
    } catch (error) {
        // Durante a transição o JWT no CONNECT continua a autenticar: ficar sem ticket
        // não pode impedir a ligação (e nos transportes XHR o ticket nem é consumido).
        console.warn("[ws-ticket] Sem ticket; a ligação segue só com o JWT.", error);
        return null;
    }
}

/** Acrescenta o ticket à URL do SockJS, preservando a query que já exista. */
export function withWsTicket(endpoint: string, ticket: string | null): string {
    if (!ticket) return endpoint;
    const separator = endpoint.includes("?") ? "&" : "?";
    return `${endpoint}${separator}ticket=${encodeURIComponent(ticket)}`;
}

/**
 * Handshake SockJS com ticket: o stompjs chama `beforeConnect` em cada (re)ligação, que pede
 * um ticket novo antes de abrir o socket. O JWT continua no CONNECT, montado depois do pedido
 * do ticket para já levar o token renovado se esse pedido tiver passado por um refresh.
 */
export function ticketedSockJs(endpoint: string): Pick<StompConfig, "beforeConnect" | "webSocketFactory"> {
    let ticket: string | null = null;
    return {
        beforeConnect: async (client) => {
            ticket = await fetchWsTicket();
            client.connectHeaders = buildStompAuthHeaders();
        },
        webSocketFactory: () => new SockJS(withWsTicket(endpoint, ticket)),
    };
}
